import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useDisable } from "../../../hooks/use-disable";
import { useActivityLog } from "../../../hooks/use-activity-log";
import { useStateContext } from "../../../contexts/auth-context";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

import { Stack } from "@mui/material";
import LoadingUI from "../../../components/UI/LoadingUI";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import LogisticFooter from "../../../components/Form/Logistic/Footer";
import CustomerInfo from "../../../components/Form/Logistic/CustomerInfo";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import AddressInfo from "../../../components/Form/Logistic/CustomerInfo/AddressInfo";
import ScanBarcode from "../../../components/Form/Logistic/DeliveryOrder/ScanBarcode";
import DeliveryInfo from "../../../components/Form/Logistic/DeliveryInfo/DeliveryInfo";
import DeliveryOrderHeader from "../../../components/Form/Logistic/DeliveryOrder/Header";
import DeliveryDetails from "../../../components/Form/Logistic/DeliveryOrder/DeliveryDetails";
import DeliveryOrderItemList from "../../../components/Table/Logistic/DeliveryOrder/ItemList/DeliveryOrderItemList";

import {
  IDeliveryDetails,
  IDeliveryOrder,
} from "../../../types/Logistic/deliveryOrder";
import {
  ActivityLogDocumentType,
  ActivityType,
  DeliveryOrderCreateInput,
  DeliveryOrderFindUniqueQuery,
  DeliveryOrderUpdateInput,
  useDeliveryOrderCreateMutation,
  useDeliveryOrderFindUniqueQuery,
  useDeliveryOrderUpdateMutation,
} from "../../../generated/logistic";
import {
  ItemSkuQtysQuery,
  useItemSkuQtysQuery,
} from "../../../generated/inventory";
import {
  deliveryOrderQueryFormatter,
  deliveryOrderCreatePayloadFormatter,
  deliveryOrderUpdatePayloadFormatter,
} from "../../../utils/Formatter/Logistic/DeliveryOrder";
import { IAttachment } from "../../../types/global";
import { uploadFileToS3 } from "../../../utils/s3";
import { errorMessageFormatter } from "../../../utils/Global";
import { ISalesItemList } from "../../../types/Sales";
import { useSalesItemListFormatter } from "../../../hooks/Sales/use-item-list-formatter";
import { useSalesItemFindManyQuery } from "../../../generated/sales";
import { useInventoryError } from "../../../hooks/use-inventory-error";

const DocumentInfoTab = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const documentType = "delivery_order";
  const { enqueueSnackbar } = useSnackbar();
  const [disabled, setDisabled] = useDisable();
  const { createActivityLog } = useActivityLog();
  const [barcodeList, setBarcodeList] = useState<any[]>([]);
  const [referenceItemList, setReferenceItemList] = useState<ISalesItemList[]>(
    []
  );

  const {
    state: { authUser },
  } = useStateContext();

  const graphQLClient = createGraphQLClientWithMiddleware("logistic");
  const graphQLClientSales = createGraphQLClientWithMiddleware("sales");
  const graphQLClientWms = createGraphQLClientWithMiddleware("wms");

  const {
    control,
    getValues,
    reset,
    handleSubmit,
    watch,
    formState: { dirtyFields, errors },
  } = useFormContext<IDeliveryOrder>();

  const {
    fields,
    replace,
    remove,
    update: traceUpdate,
  } = useFieldArray({
    control,
    name: "trace_entry_list",
  });

  const status = watch("aggrid_status");

  const disabledField = !["", "draft"].includes(status || "");

  const { data, isLoading, isSuccess, refetch } =
    useDeliveryOrderFindUniqueQuery<DeliveryOrderFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  const {
    data: allSkuQtys,
    isLoading: isAllSkuLoading,
    isSuccess: isSkuSuccess,
  } = useItemSkuQtysQuery<ItemSkuQtysQuery>(
    graphQLClientWms,
    {
      findManyInput: {
        where: {
          barcode: {
            in: barcodeList,
          },
        },
      },
    },
    {
      enabled: Boolean(barcodeList.length > 0),
    }
  );

  const { data: salesItemList, isLoading: itemListLoading } =
    useSalesItemFindManyQuery(
      graphQLClientSales,
      {
        input: {
          reference_document_type: state
            ? state.reference_document_list &&
              state.reference_document_list[0].document_type
            : data?.DeliveryOrderFindUnique?.reference_document_list &&
              data?.DeliveryOrderFindUnique?.reference_document_list[0]
                .document_type,
          reference_unique_id: state
            ? state.reference_document_list &&
              state.reference_document_list[0].document_unique_id
            : data?.DeliveryOrderFindUnique?.reference_document_list &&
              data?.DeliveryOrderFindUnique?.reference_document_list[0]
                .document_unique_id,
        },
      },
      {
        enabled: Boolean(
          state
            ? state.reference_document_list &&
                state.reference_document_list[0].document_unique_id
            : data?.DeliveryOrderFindUnique?.reference_document_list &&
                data?.DeliveryOrderFindUnique?.reference_document_list[0]
                  .document_unique_id
        ),
      }
    );

  const formatItemList = useSalesItemListFormatter(
    salesItemList?.SalesItemFindMany as ISalesItemList[]
  );

  useEffect(() => {
    if (id && isSuccess) {
      const { DeliveryOrderFindUnique } = data;

      const getData = async () => {
        const traceEntryList = DeliveryOrderFindUnique?.trace_entry_list;
        const allBarcode =
          traceEntryList
            ?.map((trace: any) => trace?.barcode)
            .filter((trace) => trace) || [];

        setBarcodeList(allBarcode);

        const formatted = await deliveryOrderQueryFormatter(
          DeliveryOrderFindUnique,
          allSkuQtys
        );

        reset(formatted);
      };

      getData();
    }
  }, [data, allSkuQtys, isSuccess, isSkuSuccess, reset]);

  useEffect(() => {
    if (id && status !== "draft") {
      setDisabled(true);
    }
    return () => {
      setDisabled(false);
    };
  }, [id, setDisabled, status]);

  useEffect(() => {
    if (state) {
      const { copied_unique_id, copied_id, ...otherState } = state;

      reset({
        ...otherState,
        created_date: dayjs(),
        issue_date: dayjs(),
        delivery_date: dayjs(state.delivery_date),
        exported_date: dayjs(),
      });
    }
  }, [reset, state]);

  useEffect(() => {
    if (formatItemList && formatItemList.length > 0) {
      setReferenceItemList(formatItemList);
    }
  }, [formatItemList]);

  const { mutateAsync: create, isLoading: isCreating } =
    useDeliveryOrderCreateMutation<Error>(graphQLClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    useDeliveryOrderUpdateMutation<Error>(graphQLClient);

  const draftHandler = async (data: IDeliveryOrder) => {
    if (!id) {
      try {
        const formatData = deliveryOrderCreatePayloadFormatter(data, "draft");
        const { DeliveryOrderCreate } = await create({
          input: formatData as DeliveryOrderCreateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/logistic/delivery-order/${DeliveryOrderCreate.id}`);

        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.DeliveryOrder,
          reference_id: DeliveryOrderCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "draft",
          },
        });
      } catch (error) {
        const formatError = errorMessageFormatter(error);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        const formatData = await deliveryOrderUpdatePayloadFormatter(
          data,
          "draft"
        );
        const { DeliveryOrderUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as DeliveryOrderUpdateInput,
        });

        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        const formattedDirtyFields = Object.keys(dirtyFields);

        if (formattedDirtyFields.length > 0) {
          await createActivityLog({
            activity_type: ActivityType.Edit,
            document_type: ActivityLogDocumentType.DeliveryOrder,
            reference_id: DeliveryOrderUpdate?.id,
            activity_detail: {
              secondary_operation: ActivityType.Edit,
            },
          });
        }
      } catch (error) {
        const formatError = errorMessageFormatter(error);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const issuedHandler = async (data: IDeliveryOrder) => {
    const formatData = id
      ? await deliveryOrderUpdatePayloadFormatter(data, "issued")
      : deliveryOrderCreatePayloadFormatter(data, "issued");

    if (
      formatData.trace_entry_list.some((list) => list.status !== "is_scanned")
    ) {
      return enqueueSnackbar(`กรุณาสแกนรายการสินค้า`, {
        variant: "error",
      });
    }

    if (!id) {
      try {
        const { DeliveryOrderCreate } = await create({
          input: formatData as DeliveryOrderCreateInput,
        });
        enqueueSnackbar(`สร้าง${t("logistic.delivery_order.index")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/logistic/delivery-order/${DeliveryOrderCreate.id}`);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.DeliveryOrder,
          reference_id: DeliveryOrderCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "issued",
          },
        });
      } catch (err) {
        const message = errorMessageFormatter(err);
        if (message?.split(",").length > 0) {
          message.split(",").forEach((unique_id: string) =>
            enqueueSnackbar(`${unique_id} เกินจำนวนนำออก`, {
              variant: "error",
            })
          );
        } else
          enqueueSnackbar(
            message || `สร้าง${t("logistic.delivery_order.index")}ไม่สำเร็จ`,
            {
              variant: "error",
            }
          );
      }
    } else {
      try {
        const { DeliveryOrderUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as DeliveryOrderUpdateInput,
        });
        await refetch();
        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.DeliveryOrder,
          reference_id: DeliveryOrderUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "issued",
          },
        });
        enqueueSnackbar(`สร้าง${t("logistic.delivery_order.index")}สำเร็จ`, {
          variant: "success",
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `สร้าง${t("logistic.delivery_order.index")}สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    }
  };

  const editHandler = async (data: IDeliveryOrder) => {
    try {
      const formatData = await deliveryOrderUpdatePayloadFormatter(
        data,
        data?.main_status || ""
      );

      const { DeliveryOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: formatData as DeliveryOrderUpdateInput,
      });

      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      if (formattedDirtyFields.length > 0) {
        await createActivityLog({
          activity_type: ActivityType.Edit,
          document_type: ActivityLogDocumentType.DeliveryOrder,
          reference_id: DeliveryOrderUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          },
        });
      }

      setDisabled(true);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];

      const { DeliveryOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          reference_document_list: data.reference_document_list,
          branch_id: data.branch_id,
          flag_status: flag_status,
        } as DeliveryOrderUpdateInput,
      });

      enqueueSnackbar(`${t("button.cancel")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.DeliveryOrder,
        reference_id: DeliveryOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.cancel")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const deliveringHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("delivering")
            ? [...data.flag_status]
            : [...data.flag_status, "delivering"]
          : ["delivering"];

      const { DeliveryOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          reference_document_list: data.reference_document_list,
          branch_id: data.branch_id,
          flag_status: flag_status,
          aggrid_status: "delivering",
        } as DeliveryOrderUpdateInput,
      });

      enqueueSnackbar(`${t("status.delivering")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.DeliveryOrder,
        reference_id: DeliveryOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "delivering",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("status.delivering")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const updateDeliveryStatusHandler = async (data: IDeliveryDetails) => {
    try {
      const mainData = getValues();

      const { attachment_list } = data;
      let uploadedAttachment: IAttachment[] = [];

      if (attachment_list && attachment_list.length > 0) {
        for (const file of attachment_list) {
          if (file instanceof File) {
            const { Location } = await uploadFileToS3(
              file,
              "deliveryOrder",
              mainData?.unique_id || ""
            );
            const formatAttachment: IAttachment = {
              attachment_name: file.attachment_name,
              uploaded_by: file.uploaded_by,
              uploaded_date: file.uploaded_date,
              url: Location,
            };
            uploadedAttachment.push(formatAttachment);
          } else {
            uploadedAttachment.push(file);
          }
        }
      }

      const currentStatus = data.is_delivery_success
        ? "delivery_success"
        : "delivery_fail";

      const { DeliveryOrderUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          reference_document_list: mainData.reference_document_list,
          branch_id: mainData.branch_id,
          aggrid_status: currentStatus,
          delivery_detail: { ...data, attachment_list: uploadedAttachment },
        } as DeliveryOrderUpdateInput,
      });

      enqueueSnackbar(
        "เปลี่ยนสถานะเป็น" + t(`status.${DeliveryOrderUpdate.aggrid_status}`),
        {
          variant:
            DeliveryOrderUpdate.aggrid_status === "delivery_success"
              ? "success"
              : "error",
        }
      );

      await refetch();

      const edit = mainData.aggrid_status === currentStatus;

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: edit ? ActivityType.Edit : ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.DeliveryOrder,
        reference_id: DeliveryOrderUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: edit ? undefined : mainData.aggrid_status,
          curr_status: edit ? undefined : currentStatus,
        },
      });
    } catch (err) {
      console.error(err);
      // enqueueSnackbar(t("status.delivery_fail"), {
      //   variant: "error",
      // });
    }
  };

  const editClickHandler = () => {
    setDisabled(false);
  };

  const cancelEditHandler = () => {
    setDisabled(true);
    reset();
  };

  const renderButton = (status: string | undefined) => {
    switch (status) {
      case "draft":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            <CustomizedButton
              title={t("inventory.goods_issue.index")}
              variant="contained"
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(issuedHandler)}
            />
          </>
        );
      case "issued":
        if (!disabled) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                onClick={cancelEditHandler}
                disabled={isUpdating}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={handleSubmit(editHandler)}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          return (
            <CustomizedButton
              variant="contained"
              title={t("status.delivering")}
              onClick={handleSubmit(deliveringHandler)}
              disabled={isUpdating}
            />
          );
        }
      case "accepted":
      case "cancelled":
      case "approved":
      case "finished":
        return;
      default:
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            <CustomizedButton
              title={t("inventory.goods_issue.index")}
              variant="contained"
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(issuedHandler)}
            />
          </>
        );
    }
  };

  useInventoryError(errors);

  if (
    id &&
    (isLoading ||
      isCreating ||
      isUpdating ||
      (barcodeList.length > 0 && isAllSkuLoading) ||
      itemListLoading)
  ) {
    return <LoadingUI />;
  }

  return (
    <>
      <DeliveryOrderHeader
        editClickHandler={editClickHandler}
        cancelHandler={cancelHandler}
        disabledField={disabledField}
        updateStatusHandler={updateDeliveryStatusHandler}
      />
      <DeliveryInfo />
      <CustomerInfo disabledField={disabledField} />
      <AddressInfo documentType={documentType} disabledField={disabledField} />
      <ScanBarcode fields={fields} update={traceUpdate} />
      <DeliveryOrderItemList
        fields={fields}
        update={traceUpdate}
        replace={replace}
        remove={remove}
        referenceItemList={referenceItemList}
      />
      <LogisticFooter documentType={documentType} />
      {["delivery_success", "delivery_fail"].includes(status || "") && (
        <DeliveryDetails />
      )}
      <BottomNavbar>
        {(!disabled || status === "issued") && (
          <Stack direction="row" spacing={1} alignItems="center">
            {renderButton(status)}
          </Stack>
        )}
      </BottomNavbar>
    </>
  );
};

export default DocumentInfoTab;
